import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
    SettingDefinition,
    SettingWithValue,
    SettingValue,
    SettingValidationResult,
    SettingCategory,
    CreateSettingDefinitionRequest,
    UpdateSettingDefinitionRequest,
    SetSettingValueRequest,
    BulkSetSettingsRequest,
    GroupedSettings,
    CATEGORY_DISPLAY_NAMES
} from '../types/settings';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class SettingsService {
    private baseUrl = `${API_BASE_URL}/settings`;
    private definitionsUrl = `${API_BASE_URL}/setting-definitions`;

    // ==================== Definitions ====================

    /**
     * Get all setting definitions
     */
    async getDefinitions(): Promise<SettingDefinition[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/definitions`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get setting definitions by category
     */
    async getDefinitionsByCategory(category: string): Promise<SettingDefinition[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/definitions/category/${category}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get available categories
     */
    async getCategories(): Promise<SettingCategory[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/categories`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    // ==================== System Settings ====================

    /**
     * Get all system settings with their values
     */
    async getSystemSettings(): Promise<SettingWithValue[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/system`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get system settings by category
     */
    async getSystemSettingsByCategory(category: string): Promise<SettingWithValue[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/system/category/${category}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get a specific system setting
     */
    async getSystemSetting(settingKey: string): Promise<SettingValue | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/system/${settingKey}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    /**
     * Set a system setting value
     */
    async setSystemSetting(settingKey: string, value?: string): Promise<SettingValue> {
        const request: SetSettingValueRequest = { value };
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/system/${settingKey}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Set multiple system settings at once
     */
    async setSystemSettings(settings: Record<string, string | undefined>): Promise<SettingValue[]> {
        const request: BulkSetSettingsRequest = { settings };
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/system`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Reset a system setting to default
     */
    async resetSystemSetting(settingKey: string): Promise<boolean> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/system/${settingKey}`);
        return response.ok;
    }

    // ==================== Profile Settings ====================

    /**
     * Get all profile settings for current user
     */
    async getProfileSettings(): Promise<SettingWithValue[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/profile`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get profile settings for a specific user
     */
    async getProfileSettingsForUser(userId: string): Promise<SettingWithValue[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/profile/user/${userId}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get profile settings by category for current user
     */
    async getProfileSettingsByCategory(category: string): Promise<SettingWithValue[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/profile/category/${category}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get a specific profile setting for current user
     */
    async getProfileSetting(settingKey: string): Promise<SettingValue | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/profile/${settingKey}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    /**
     * Set a profile setting value for current user
     */
    async setProfileSetting(settingKey: string, value?: string): Promise<SettingValue> {
        const request: SetSettingValueRequest = { value };
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/profile/${settingKey}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Set a profile setting value for a specific user
     */
    async setProfileSettingForUser(userId: string, settingKey: string, value?: string): Promise<SettingValue> {
        const request: SetSettingValueRequest = { value };
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/profile/user/${userId}/${settingKey}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Set multiple profile settings at once for current user
     */
    async setProfileSettings(settings: Record<string, string | undefined>): Promise<SettingValue[]> {
        const request: BulkSetSettingsRequest = { settings };
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/profile`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Reset a profile setting to system/default for current user
     */
    async resetProfileSetting(settingKey: string): Promise<boolean> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/profile/${settingKey}`);
        return response.ok;
    }

    // ==================== Effective Values ====================

    /**
     * Get effective setting with full resolution info
     */
    async getEffectiveSetting(settingKey: string): Promise<SettingWithValue | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/effective/${settingKey}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    // ==================== Validation ====================

    /**
     * Validate a value for a setting
     */
    async validateValue(settingKey: string, value?: string): Promise<SettingValidationResult> {
        const request: SetSettingValueRequest = { value };
        const response = await AuthService.authenticatedPost(`${this.baseUrl}/validate/${settingKey}`, request);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    // ==================== Setting Definitions (Admin) ====================

    /**
     * Get all setting definitions (admin)
     */
    async getAllDefinitions(): Promise<SettingDefinition[]> {
        const response = await AuthService.authenticatedGet(this.definitionsUrl);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get a specific setting definition
     */
    async getDefinition(settingKey: string): Promise<SettingDefinition | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.definitionsUrl}/${settingKey}`);
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) return null;
            throw error;
        }
    }

    /**
     * Create a new setting definition
     */
    async createDefinition(request: CreateSettingDefinitionRequest): Promise<SettingDefinition> {
        const response = await AuthService.authenticatedPost(this.definitionsUrl, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Update a setting definition
     */
    async updateDefinition(settingKey: string, request: UpdateSettingDefinitionRequest): Promise<SettingDefinition> {
        const response = await AuthService.authenticatedPut(`${this.definitionsUrl}/${settingKey}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    /**
     * Deactivate a setting definition
     */
    async deactivateDefinition(settingKey: string): Promise<boolean> {
        const response = await AuthService.authenticatedDelete(`${this.definitionsUrl}/${settingKey}`);
        return response.ok;
    }

    // ==================== Utility Methods ====================

    /**
     * Group settings by category
     */
    groupByCategory(settings: SettingWithValue[]): GroupedSettings[] {
        const grouped = new Map<string, SettingWithValue[]>();

        for (const setting of settings) {
            const category = setting.definition.category;
            if (!grouped.has(category)) {
                grouped.set(category, []);
            }
            grouped.get(category)!.push(setting);
        }

        return Array.from(grouped.entries())
            .map(([category, settings]) => ({
                category,
                displayName: CATEGORY_DISPLAY_NAMES[category] || category,
                settings: settings.sort((a, b) => a.definition.displayOrder - b.definition.displayOrder)
            }))
            .sort((a, b) => a.category.localeCompare(b.category));
    }

    /**
     * Parse a setting value based on data type
     */
    parseValue(value: string | undefined, dataType: string): any {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        switch (dataType.toLowerCase()) {
            case 'bool':
                return value === 'true' || value === '1';
            case 'int':
                return parseInt(value, 10);
            case 'decimal':
                return parseFloat(value);
            case 'datetime':
                return new Date(value);
            case 'json':
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            default:
                return value;
        }
    }

    /**
     * Format a value for display
     */
    formatValue(value: string | undefined, dataType: string): string {
        if (value === undefined || value === null || value === '') {
            return '-';
        }

        switch (dataType.toLowerCase()) {
            case 'bool':
                return value === 'true' || value === '1' ? 'Ja' : 'Nei';
            case 'datetime':
                try {
                    return new Date(value).toLocaleString('nb-NO');
                } catch {
                    return value;
                }
            default:
                return value;
        }
    }
}

export const settingsService = new SettingsService();
